import React, {useState, useEffect, useCallback} from 'react';
import {getForm, submitForm} from './api/forms';
import FormItem from './FormItem';
import {Helmet} from 'react-helmet';
import {QRCodeCanvas} from 'qrcode.react';
import {isWechatBrowser} from './config/wechat';

// 将分享图片URL规范化为完整公网URL
const normalizeShareImgUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) return img;
    const base = window.location.origin.replace(/\/+$/, '');
    return base + (img.startsWith('/') ? img : '/' + img);
};
// 临时移除 authRedirectManager 导入，使用原有逻辑
// import { 
//     checkWechatLoginStatus, 
//     needsWechatAuth, 
//     startAuthRedirect, 
//     redirectToWechatAuth,
//     isAuthRedirectProcessing 
// } from '../utils/authRedirectManager';

// 检测是否为移动设备
const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.innerWidth <= 768;
};


export default function FormFiller({formId, onFormDataLoaded, formData, previewMode, cardNumber}) {
    const [formSchema, setFormSchema] = useState(null);
    
    // 调试：显示接收到的卡号参数
    useEffect(() => {
        if (cardNumber) {
            console.log('🎫 FormFiller: 接收到卡号参数:', cardNumber);
        }
    }, [cardNumber]);
    const [wechatShare, setWechatShare] = useState({});
    const [formValues, setFormValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    const [formName, setFormName] = useState('表单填写');
    const [submitted, setSubmitted] = useState(false);
    const [isMobileDevice, setIsMobileDevice] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [formPathUrl, setFormPathUrl] = useState('');
    const [error, setError] = useState('');
    const isDev = process.env.NODE_ENV === 'development';
    const isWechat = isWechatBrowser();

    // 检测移动设备
    useEffect(() => {
        setIsMobileDevice(isMobile());
    }, []);

    // 检测非微信环境并显示微信扫码弹窗
    useEffect(() => {
        if (!isWechat && formId && !error) {
            setShowQRModal(true); // 只要不是微信浏览器都弹窗，且没有error提示
        }
    }, [formId, isWechat, error]);

    // 检查登录状态 - 简化版
    useEffect(() => {
        console.log('🔍 FormFiller: 开始检查登录状态');

        // 检查当前页面是否已经是授权页面，避免循环
        if (window.location.pathname === '/wechat-auth' || window.location.pathname === '/wechat-callback' || window.location.pathname === '/wechat-auth-callback') {
            console.log('⚠️ FormFiller: 当前已在授权相关页面，跳过登录检查');
            return;
        }

        const wechatUser = localStorage.getItem('wechatUser');
        const hasWechatNickname = wechatUser && JSON.parse(wechatUser)?.nickname;
        const isJump = new URLSearchParams(window.location.search).get('jump') === '1';
        const authSuccess = new URLSearchParams(window.location.search).get('auth_success') === '1';
        const authCompleted = localStorage.getItem('wechatAuthCompleted') === '1';

        // 检查微信用户信息
        console.log('👤 从localStorage获取的微信用户信息:', wechatUser);
        
        // 如果是授权回调（jump=1），清理相关状态
        if (isJump || authSuccess || authCompleted) {
            console.log('🔄 FormFiller: 检测到授权回调，清理跳转状态');
            
            // 延迟清理，确保页面加载完成
            setTimeout(() => {
                localStorage.removeItem('returnUrl');
                localStorage.removeItem('wechatReturnUrl');
                localStorage.removeItem('isRedirectingToAuth');
                localStorage.removeItem('isRedirectingToWechat');
                localStorage.removeItem('isProcessingWechatAuth');
                localStorage.removeItem('wechatAuthCompleted');
                localStorage.removeItem('wechatAuthTimestamp');
                console.log('🧹 FormFiller: 授权回调状态已清理');
            }, 500);
            
            // 如果是第一次授权完成，等待一下确保状态同步
            if (authSuccess || authCompleted) {
                console.log('🎉 FormFiller: 检测到第一次授权完成，等待状态同步');
                
                // 等待一下再检查用户信息，确保localStorage已同步
                setTimeout(() => {
                    const updatedWechatUser = localStorage.getItem('wechatUser');
                    const updatedHasNickname = updatedWechatUser && JSON.parse(updatedWechatUser)?.nickname;
                    console.log('🔄 FormFiller: 延迟检查用户信息:', updatedHasNickname ? '已获取' : '未获取');
                }, 200);
            }
        }

        // 如果没有nickname，自动清除跳转标记
        if (!hasWechatNickname) {
            localStorage.removeItem('isRedirectingToAuth');
            localStorage.removeItem('isRedirectingToWechat');
            console.log('🧹 已清除跳转标记（无有效昵称）');
        }

        // 如果有昵称或者是授权回调，跳过授权检查
        if (hasWechatNickname || isJump || authSuccess || authCompleted) {
            console.log('✅ FormFiller: 已有微信昵称或授权回调，跳过微信授权校验');
            return;
        }

        // 检查是否需要微信授权
        if (formId) {
            const isWechat = isWechatBrowser();
            const isLoggedIn = localStorage.getItem('isLoggedIn') === '1';
            const isWechatLoggedIn = localStorage.getItem('isWechatLoggedIn') === '1';
            
            // 微信浏览器：检查微信登录状态（jump=1 时跳过鉴权）
            if (isWechat && (!isWechatLoggedIn || !wechatUser) && !isJump) {
                console.log('⚠️ FormFiller: 微信用户未登录，准备跳转到微信授权页面');
                // 检查是否已经在跳转过程中，避免重复跳转
                const isRedirecting = localStorage.getItem('isRedirectingToAuth');
                console.log('🔍 检查是否已在跳转过程中:', isRedirecting);
                if (isRedirecting) {
                    console.log('⚠️ FormFiller: 已经在跳转过程中，跳过重复跳转');
                    return;
                }
                // 保存跳转相关信息
                const currentPath = window.location.pathname + window.location.search;
                // 简单的路径检查：如果包含需要授权的路径，则保存
                const needsAuth = currentPath.includes('/detail') || currentPath.includes('/fill');
                if (needsAuth) {
                    const returnUrl = window.location.href; // 使用完整URL
                    
                    // 保存跳转URL
                    localStorage.setItem('returnUrl', returnUrl);
                    localStorage.setItem('wechatReturnUrl', returnUrl); // 兼容旧版本
                    localStorage.setItem('isRedirectingToAuth', 'true');
                    
                    // const currentDomain = window.location.origin; // 原动态域名
                    // const currentDomain = 'https://form.fangxiaojia.vip/microform-test'; // 临时测试域名
                    const currentDomain = window.location.origin; // 动态获取当前域名
                    const authUrl = `${currentDomain}/wechat-auth`;
                    
                    // 仅在非生产环境输出调试信息
                    if (process.env.NODE_ENV !== 'production') {
                        console.log('🚀 FormFiller: 跳转到微信授权页面:', authUrl);
                    }
                    
                    try {
                        window.location.replace(authUrl);
                    } catch (error) {
                        console.error('FormFiller: 跳转失败:', error);
                        // 备用跳转方式
                        window.location.href = authUrl;
                    }
                    return;
                } else {
                    console.log('🔍 FormFiller: 当前页面不需要微信授权，不进行跳转');
                }
            }
        }
        
        console.log('✅ FormFiller: 用户已登录，继续显示表单');
    }, [formId]);

    // 表单加载函数
    const loadForm = useCallback(async (formId) => {
        try {
            console.log(`🔄 FormFiller: 开始加载表单 ${formId}`);
            const res = await getForm(formId);
            
            console.log('📋 FormFiller: 表单数据响应:', res);
            
            // 解析表单数据
            let schema = null;
            let wechatShare = null;
            
            if (res.data && res.data.formSchema) {
                schema = typeof res.data.formSchema === 'string' ? JSON.parse(res.data.formSchema) : res.data.formSchema;
            } else if (res.formSchema) {
                schema = typeof res.formSchema === 'string' ? JSON.parse(res.formSchema) : res.formSchema;
            }
            
            if (res.data && res.data.wechatShare) {
                wechatShare = typeof res.data.wechatShare === 'string' ? JSON.parse(res.data.wechatShare) : res.data.wechatShare;
            } else if (res.wechatShare) {
                wechatShare = typeof res.wechatShare === 'string' ? JSON.parse(res.wechatShare) : res.wechatShare;
            }
            
            if (schema) {
                console.log('✅ FormFiller: 表单数据解析成功');
                setFormName(schema.title || schema.formTitle || '表单填写');
                setFormSchema({
                    title: schema.title || schema.formTitle,
                    formTitle: schema.title || schema.formTitle,
                    desc: schema.formDesc || schema.description,
                    formDesc: schema.formDesc || schema.description,
                    components: schema.components || [],
                    showTitle: schema.showTitle !== false,
                    formMessage: schema.formMessage || '提交成功',
                    region: res.data?.region || schema.region
                });
                
                // 设置微信分享信息
                setWechatShare(wechatShare || {});
                
                // 初始化表单默认值
                const initialValues = {};
                (schema.components || []).forEach(comp => {
                    initialValues[comp.id] = comp.defaultValue ?? '';
                });
                
                // 如果有卡号参数，预填充到相关字段
                if (cardNumber) {
                    (schema.components || []).forEach(comp => {
                        // 优先检查组件类型是否为 vip_card_number
                        if (comp.type === 'vip_card_number') {
                            initialValues[comp.id] = cardNumber;
                            console.log(`预填充卡号到vip_card_number字段: ${comp.label || comp.id}`);
                            return;
                        }
                        
                        // 如果没有vip_card_number类型，则使用原有的匹配逻辑
                        const fieldLabel = comp.label || comp.fieldName || '';
                        const fieldId = comp.id || '';
                        
                        if (fieldLabel.includes('卡号') || fieldLabel.includes('卡') || 
                            fieldId.includes('card') || fieldId.includes('number') ||
                            fieldLabel.includes('特权卡') || fieldLabel.includes('黑卡')) {
                            initialValues[comp.id] = cardNumber;
                            console.log(`预填充卡号到字段: ${fieldLabel} (${fieldId})`);
                        }
                    });
                }
                
                setFormValues(initialValues);
                
                // 回调父组件
                if (onFormDataLoaded) {
                    onFormDataLoaded({
                        ...res.data,
                        formSchema: schema,
                        wechatShare: wechatShare
                    });
                }
                
                setError('');
                console.log('🎉 FormFiller: 表单加载完成');
            } else {
                throw new Error('表单数据格式错误');
            }
        } catch (error) {
            console.error('❌ FormFiller: 表单加载失败:', error);
            const errorMessage = error.message || '表单加载失败，请稍后重试';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [cardNumber, onFormDataLoaded]);

    useEffect(() => {
        if (previewMode && formData) {
            // 预览模式：直接使用传入的formData
            setFormName(formData.title || '表单填写');
            setFormSchema({
                title: formData.title,
                formTitle: formData.title,
                desc: formData.formDesc,
                formDesc: formData.formDesc,
                components: formData.components || [],
                showTitle: formData.showTitle !== false
            });

            // 初始化表单默认值
            const initialValues = {};
            (formData.components || []).forEach(comp => {
                initialValues[comp.id] = comp.defaultValue ?? '';
            });
            
            // 如果有卡号参数，预填充到相关字段
            if (cardNumber) {
                (formData.components || []).forEach(comp => {
                    // 优先检查组件类型是否为 vip_card_number
                    if (comp.type === 'vip_card_number') {
                        initialValues[comp.id] = cardNumber;
                        console.log(`预填充卡号到vip_card_number字段: ${comp.label || comp.id}`);
                        return;
                    }
                    
                    // 如果没有vip_card_number类型，则使用原有的匹配逻辑
                    const fieldLabel = comp.label || comp.fieldName || '';
                    const fieldId = comp.id || '';
                    
                    if (fieldLabel.includes('卡号') || fieldLabel.includes('卡') || 
                        fieldId.includes('card') || fieldId.includes('number') ||
                        fieldLabel.includes('特权卡') || fieldLabel.includes('黑卡')) {
                        initialValues[comp.id] = cardNumber;
                        console.log(`预填充卡号到字段: ${fieldLabel} (${fieldId})`);
                    }
                });
            }
            
            setFormValues(initialValues);
            setLoading(false);
            return;
        }

        if (!formId) return;
        
        // 检查是否是第一次授权后的情况
        const authSuccess = new URLSearchParams(window.location.search).get('auth_success') === '1';
        const authCompleted = localStorage.getItem('wechatAuthCompleted') === '1';
        
        if (authSuccess || authCompleted) {
            console.log('🎉 FormFiller: 检测到第一次授权完成，延迟加载表单');
            // 第一次授权后，稍微延迟一下再加载表单，确保状态完全同步
            setTimeout(() => {
                setLoading(true);
                setError('');
                loadForm(formId);
            }, 300);
        } else {
            setLoading(true);
            setError('');
            loadForm(formId);
        }
    }, [formId, previewMode, formData, onFormDataLoaded, loadForm]);

    const handleInputChange = useCallback((id, value) => {
        setFormValues(prev => ({...prev, [id]: value}));
    }, []);

    // 表单提交
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitResult(null);

        // 预览模式下的处理
        if (previewMode) {
            // 简单必填校验
            const errors = [];
            (formSchema.components || []).forEach(comp => {
                if (comp.required && !formValues[comp.id]) {
                    errors.push(`${comp.label || comp.fieldName || comp.id} 为必填项`);
                }
            });
            if (errors.length > 0) {
                // 全部错误用弹窗展示
                window.toastManager && window.toastManager.error(errors.join('；'));
                setSubmitResult(null);
                setIsSubmitting(false);
                return;
            }

            // 预览模式下显示成功提示
            setSubmitResult({success: true, message: '预览模式：表单验证通过，数据已收集'});
            setIsSubmitting(false);
            return;
        }

        // 正常模式下的提交逻辑
        // 简单必填校验
        const errors = [];
        (formSchema.components || []).forEach(comp => {
            if (comp.required && !formValues[comp.id]) {
                errors.push(`${comp.label || comp.fieldName || comp.id} 为必填项`);
            }
            // member_phone类型校验
            if (comp.type === 'member_phone' && formValues[comp.id] && !/^1[3-9]\d{9}$/.test(formValues[comp.id])) {
                errors.push(`${comp.label || comp.fieldName || comp.id} 不是有效的手机号`);
            }
        });
        if (errors.length > 0) {
            setSubmitResult({success: false, message: errors.join('；')});
            setIsSubmitting(false);
            return;
        }

        // 构造提交数据
        const data = {};
        (formSchema.components || []).forEach(comp => {
            const key = comp.fieldMap || comp.label || comp.id;
            // inspection_brand 多选值用逗号拼接
            if (comp.type === 'inspection_brand' && Array.isArray(formValues[comp.id])) {
                data[key] = formValues[comp.id].join(',');
            } else if (comp.type === 'brand_store_cascader' && formValues[comp.id] && typeof formValues[comp.id] === 'object') {
                // 拆分品牌-店铺地址为两个字段
                data['品牌'] = formValues[comp.id].brand || '';
                data['店铺地址'] = formValues[comp.id].store || '';
            } else {
                data[key] = formValues[comp.id];
            }
        });

        // 将地区写入 submission_data 中
        if (formSchema && formSchema.region) {
            data['地区'] = formSchema.region;
        }

        // 获取微信用户信息
        const wechatUser = JSON.parse(localStorage.getItem('wechatUser') || '{}');
        const submitData = {
            formId,
            data,
            openId: wechatUser.openid || '',
            nickName: wechatUser.nickname || '',
            headImgUrl: wechatUser.headimgurl || ''
        };

        try {
            const res = await submitForm(submitData);
            if (res.code === 200) {
                setSubmitted(true);
                // 不再弹出成功toast
            } else {
                setSubmitResult({success: false, message: res.message || '数据提交异常，请重试'});
            }
        } catch (e) {
            // setSubmitResult({success: false, message: '网络连接异常，请检查网络后重试'});
        }
        setIsSubmitting(false);
    };

    if (loading) return <div style={{textAlign: 'center', padding: 40}}>加载中...</div>;
    if (!formSchema) return <div style={{textAlign: 'center', padding: 40, color: '#999'}}>表单不存在</div>;

    // 优先使用formData.formDesc（预览弹窗传递），否则用formSchema.desc
    const previewDesc = (formData && formData.formDesc) || formSchema.desc || formSchema.formDesc;
    // 标题显示开关（兼容 "false" 字符串）
    const contentShowTitle = !(formSchema && ((formSchema.showTitle === false) || (String(formSchema.showTitle).toLowerCase() === 'false')));
    // 仅在异常情况下输出重要日志：后端返回 false 但仍计算为显示
    if (formSchema && formSchema.showTitle === false && contentShowTitle) {
        // eslint-disable-next-line no-console
        console.warn('[FormFiller] showTitle=false but title computed visible');
    }

    // 微信分享meta内容全部用 wechatShare 字段
    const shareTitle = wechatShare.title || (formSchema && (formSchema.title || formSchema.formTitle)) || formName;
    const shareDesc = wechatShare.desc || '';
    const shareImg = normalizeShareImgUrl(wechatShare.img);

    if (isDev && !isWechat) {
        // 测试环境下，非微信浏览器直接渲染表单内容，不弹窗不提示
        return (
            <div className="form-filler" style={{
                maxWidth: '100%',
                margin: '0 auto',
                padding: '12px',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                boxSizing: 'border-box',
                position: 'relative'
            }}>
                {/* 表单头部 */}
                <div style={{
                    background: '#fff',
                    color: '#333',
                    padding: '12px 8px',
                    textAlign: 'left'
                }}>
                    {contentShowTitle && (<h1 style={{
                        color: '#333',
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: '0 0 12px 0',
                        lineHeight: '1.3'
                    }}>
                        {(formSchema && (formSchema.title || formSchema.formTitle)) || '未命名表单'}
                    </h1>)}
                    {previewDesc && (
                        <div style={{
                            fontSize: '14px',
                            lineHeight: '1.6',
                            maxWidth: '100%',
                            margin: '0',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            padding: '0',
                            boxSizing: 'border-box',
                            textAlign: 'left',
                            position: 'relative'
                        }}>
                            <style>{`
      .form-description img {
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        display: block;
        margin: 0 !important;
        padding: 0 !important;
        box-sizing: border-box;
      }
    `}</style>
                            <div className="form-description" dangerouslySetInnerHTML={{__html: previewDesc}}/>
                        </div>
                    )}
                </div>
                {/* 表单内容 */}
                <form
                    style={{
                        padding: '12px 8px',
                        background: '#fff',
                        width: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                    }}
                    onSubmit={handleSubmit}
                >
                    {/* 移动端检测提示（仅开发环境显示） */}
                    {process.env.NODE_ENV === 'development' && (
                        <div style={{
                            background: isMobileDevice ? '#e6f7ff' : '#fff7e6',
                            border: isMobileDevice ? '1px solid #91d5ff' : '1px solid #ffd591',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            marginBottom: '16px',
                            fontSize: '12px',
                            color: isMobileDevice ? '#1890ff' : '#d46b08',
                            textAlign: 'center'
                        }}>
                            {isMobileDevice ? '📱 移动端模式' : '💻 桌面端模式'}
                        </div>
                    )}
                    {(formSchema.components || []).map(comp => (
                        <div key={comp.id} style={{
                            marginBottom: '16px',
                            display: comp.hidden ? 'none' : 'block',
                            width: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden'
                        }}>
                            {(comp.label || comp.fieldName || comp.id) && (
                                <label style={{
                                    display: 'block',
                                    marginBottom: '4px',
                                    fontWeight: '600',
                                    color: '#333',
                                    fontSize: '14px',
                                    lineHeight: '1.3'
                                }}>
                                    {comp.label || comp.fieldName || comp.id}
                                    {comp.required && <span style={{color: '#ff7875', marginLeft: '3px'}}> *</span>}
                                </label>
                            )}
                            <FormItem comp={comp} value={formValues[comp.id] ?? ''} onChange={handleInputChange}/>
                        </div>
                    ))}
                    <div style={{textAlign: 'center', marginTop: '24px'}}>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '14px 40px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '16px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1,
                                fontWeight: '600',
                                boxShadow: '0 3px 12px rgba(102, 126, 234, 0.3)',
                                transition: 'all 0.3s ease',
                                minWidth: '180px',
                                width: '100%',
                                maxWidth: '300px'
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.4)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 3px 12px rgba(102, 126, 234, 0.3)';
                                }
                            }}
                        >
                            {isSubmitting ? '提交中...' : (previewMode ? '预览提交' : '提交表单')}
                        </button>
                    </div>
                    {submitResult && (
                        <div style={{
                            color: submitResult.success ? '#52c41a' : '#ff7875',
                            textAlign: 'center',
                            marginTop: '16px',
                            padding: '10px 14px',
                            background: submitResult.success ? '#f6ffed' : '#fff2f0',
                            borderRadius: '8px',
                            border: `1px solid ${submitResult.success ? '#b7eb8f' : '#ffccc7'}`,
                            fontSize: '13px'
                        }}>
                            {submitResult.message}
                        </div>
                    )}
                </form>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="form-filler" style={{
                maxWidth: '100%',
                margin: '0 auto',
                padding: '16px',
                textAlign: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                boxSizing: 'border-box'
            }}>
                <Helmet>
                    <title>{formName}</title>
                    <meta name="description" content={shareDesc}/>
                    <meta property="og:title" content={shareTitle}/>
                    <meta property="og:description" content={shareDesc}/>
                    <meta property="og:image" content={shareImg}/>
                    <meta property="og:type" content="website"/>
                    <meta property="og:url" content={window.location.href}/>
                    <meta name="wechat-title" content={shareTitle}/>
                    <meta name="wechat-desc" content={shareDesc}/>
                    <meta name="wechat-img" content={shareImg}/>
                </Helmet>
                <div style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '32px 24px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    width: '100%',
                    maxWidth: '400px'
                }}>
                    <div style={{fontSize: '40px', color: '#52c41a', margin: '0 0 16px 0'}}>✔</div>
                    <div style={{
                        fontSize: '20px',
                        color: '#222',
                        marginBottom: '12px',
                        fontWeight: '600'
                    }}>{formSchema.formMessage || '提交成功'}</div>
                    <div style={{fontSize: '14px', color: '#666'}}>感谢您的参与</div>
                </div>
            </div>
        );
    }

    return (
        <div className="form-filler" style={{
            maxWidth: '100%',
            margin: '0 auto',
            padding: '12px',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            boxSizing: 'border-box',
            position: 'relative'
        }}>
            {/* 错误提示 */}
            {error && (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '32px 24px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    width: '100%',
                    maxWidth: '400px',
                    textAlign: 'center',
                    zIndex: 10000
                }}>
                    <div style={{fontSize: '40px', color: '#ff7875', margin: '0 0 16px 0'}}>⚠️</div>
                    <div style={{fontSize: '18px', color: '#222', marginBottom: '12px', fontWeight: '600'}}>访问提示
                    </div>
                    <div
                        style={{fontSize: '14px', color: '#666', marginBottom: '24px', lineHeight: '1.5'}}>{error}</div>
                    <button
                        onClick={() => window.history.back()}
                        style={{
                            padding: '12px 24px',
                            background: '#1890ff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        返回
                    </button>
                </div>
            )}
            {/* 二维码弹窗 */}
            {showQRModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(255,255,255,0.96)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <div style={{
                            marginBottom: 24,
                            color: '#bbb',
                            fontSize: 18,
                            fontWeight: 500,
                            textAlign: 'center'
                        }}>
                            <span style={{
                                display: 'inline-block',
                                background: '#f5f5f5',
                                borderRadius: 20,
                                padding: '6px 18px',
                                marginBottom: 16
                            }}>
                                <span style={{fontSize: 16, marginRight: 6}}>ⓘ</span>
                                当前无法直接查看页面
                            </span>
                        </div>
                        <QRCodeCanvas
                            value={formPathUrl || window.location.href}
                            size={180}
                            includeMargin={true}
                            style={{
                                marginBottom: 24,
                                border: '1px solid #eee',
                                borderRadius: 12,
                                background: '#fafafa'
                            }}
                        />
                        <div style={{
                            fontSize: 18,
                            color: '#222',
                            fontWeight: 600,
                            marginBottom: 0,
                            textAlign: 'center'
                        }}>
                            请打开微信，扫描二维码来继续访问
                        </div>
                    </div>
                </div>
            )}
            <Helmet>
                <title>{formName}</title>
                <meta name="description" content={shareDesc}/>
                <meta property="og:title" content={shareTitle}/>
                <meta property="og:description" content={shareDesc}/>
                <meta property="og:image" content={shareImg}/>
                <meta property="og:type" content="website"/>
                <meta property="og:url" content={window.location.href}/>
                <meta name="wechat-title" content={shareTitle}/>
                <meta name="wechat-desc" content={shareDesc}/>
                <meta name="wechat-img" content={shareImg}/>
            </Helmet>

            <div style={{
                maxWidth: '100%',
                margin: '0 auto',
                background: '#fff',
                overflow: 'hidden'
            }}>
                {/* 表单头部 */}
                <div style={{
                    background: '#fff',
                    color: '#333',
                    padding: '12px 8px',
                    textAlign: 'left'
                }}>
                    {contentShowTitle && (<h1 style={{
                        color: '#333',
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: '0 0 12px 0',
                        lineHeight: '1.3'
                    }}>
                        {(formSchema && (formSchema.title || formSchema.formTitle)) || '未命名表单'}
                    </h1>)}
                    {previewDesc && (
                        <div style={{
                            fontSize: '14px',
                            lineHeight: '1.6',
                            maxWidth: '100%',
                            margin: '0',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            padding: '0',
                            boxSizing: 'border-box',
                            textAlign: 'left',
                            position: 'relative'
                        }}>
                            <style>{`
      .form-description img {
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        display: block;
        margin: 0 !important;
        padding: 0 !important;
        box-sizing: border-box;
      }
    `}</style>
                            <div className="form-description" dangerouslySetInnerHTML={{__html: previewDesc}}/>
                        </div>
                    )}
                </div>

                {/* 表单内容 */}
                <form
                    style={{
                        padding: '12px 8px',
                        background: '#fff',
                        width: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                    }}
                    onSubmit={handleSubmit}
                >
                    {/* 移动端检测提示（仅开发环境显示） */}
                    {process.env.NODE_ENV === 'development' && (
                        <div style={{
                            background: isMobileDevice ? '#e6f7ff' : '#fff7e6',
                            border: isMobileDevice ? '1px solid #91d5ff' : '1px solid #ffd591',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            marginBottom: '16px',
                            fontSize: '12px',
                            color: isMobileDevice ? '#1890ff' : '#d46b08',
                            textAlign: 'center'
                        }}>
                            {isMobileDevice ? '📱 移动端模式' : '💻 桌面端模式'}
                        </div>
                    )}
                    {(formSchema.components || []).map(comp => (
                        <div key={comp.id} style={{
                            marginBottom: '16px',
                            display: comp.hidden ? 'none' : 'block',
                            width: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden'
                        }}>
                            {(comp.label || comp.fieldName || comp.id) && (
                                <label style={{
                                    display: 'block',
                                    marginBottom: '4px',
                                    fontWeight: '600',
                                    color: '#333',
                                    fontSize: '14px',
                                    lineHeight: '1.3'
                                }}>
                                    {comp.label || comp.fieldName || comp.id}
                                    {comp.required && <span style={{color: '#ff7875', marginLeft: '3px'}}> *</span>}
                                </label>
                            )}
                            <FormItem comp={comp} value={formValues[comp.id] ?? ''} onChange={handleInputChange}/>
                        </div>
                    ))}

                    <div style={{textAlign: 'center', marginTop: '24px'}}>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '14px 40px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '16px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1,
                                fontWeight: '600',
                                boxShadow: '0 3px 12px rgba(102, 126, 234, 0.3)',
                                transition: 'all 0.3s ease',
                                minWidth: '180px',
                                width: '100%',
                                maxWidth: '300px'
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.4)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 3px 12px rgba(102, 126, 234, 0.3)';
                                }
                            }}
                        >
                            {isSubmitting ? '提交中...' : (previewMode ? '预览提交' : '提交表单')}
                        </button>
                    </div>

                    {submitResult && (
                        <div style={{
                            color: submitResult.success ? '#52c41a' : '#ff7875',
                            textAlign: 'center',
                            marginTop: '16px',
                            padding: '10px 14px',
                            background: submitResult.success ? '#f6ffed' : '#fff2f0',
                            borderRadius: '8px',
                            border: `1px solid ${submitResult.success ? '#b7eb8f' : '#ffccc7'}`,
                            fontSize: '13px'
                        }}>
                            {submitResult.message}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}